
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Sparkles } from "lucide-react";
import { VacationMascot } from "./VacationMascot";
import { SmartPopularSuggestions } from "./SmartPopularSuggestions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { executeSearchStrategy } from "./search/SearchOrchestrator";
import { toast } from "sonner";

interface VacationSearchProps {
  onSearch?: (query: string) => void;
  onResults?: (results: any[]) => void;
  loading?: boolean;
  showResults?: boolean;
}

export const VacationSearch = ({ onSearch, onResults, loading = false, showResults = true }: VacationSearchProps) => {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim());
      } else if (onResults && showResults) {
        // Use the same smart search logic as homepage
        setIsSearching(true);
        try {
          await executeSearchStrategy(query.trim(), (results) => {
            onResults(results);
          });
        } catch (error) {
          console.error('Search error:', error);
          toast.error("Er ging iets mis bij het zoeken");
        } finally {
          setIsSearching(false);
        }
      } else {
        // Navigate to results page
        navigate(`/resultaten?q=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    setQuery(suggestion);
    if (onSearch) {
      onSearch(suggestion);
    } else if (onResults && showResults) {
      // Use the same smart search logic as homepage
      setIsSearching(true);
      try {
        await executeSearchStrategy(suggestion, (results) => {
          onResults(results);
        });
      } catch (error) {
        console.error('Search error:', error);
        toast.error("Er ging iets mis bij het zoeken");
      } finally {
        setIsSearching(false);
      }
    } else {
      navigate(`/resultaten?q=${encodeURIComponent(suggestion)}`);
    }
  };

  const currentLoading = loading || isSearching;

  if (currentLoading) {
    return (
      <div className="max-w-4xl mx-auto text-center space-y-8">
        <div className="flex items-center justify-center gap-6 mb-8">
          <div>
            <div className="flex items-center justify-center gap-4 mb-4">
              <VacationMascot size={isMobile ? "small" : "large"} />
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-orange-600 mb-2">
              Zonnie zoekt nu jouw volgende vakantie!
            </h2>
            <p className="text-lg text-gray-600">
              Even geduld, we zoeken de beste matches voor je...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto text-center space-y-8">
      <div className="flex items-center justify-center gap-6 mb-8">
        <div>
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 bg-clip-text text-transparent mb-4 font-poppins">
            Vind jouw perfecte vakantie
          </h1>
          <p className="text-xl text-gray-600 font-medium">
            Ontdek je volgende vakantie met onze Zonnie
          </p>
        </div>
        <div className="flex-shrink-0">
          <VacationMascot size={isMobile ? "small" : "large"} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-200"></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-2 border border-orange-100">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <Input
                type="text"
                placeholder={isMobile ? "" : "Bijv. 'All inclusive Turkije' of 'Romantisch Parijs'"}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 border-0 text-lg bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
                disabled={currentLoading}
              />
              <Button
                type="submit"
                disabled={!query.trim() || currentLoading}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-3 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {currentLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Zoeken...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    <span>Zoeken</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>

      <SmartPopularSuggestions onSuggestionClick={handleSuggestionClick} />
    </div>
  );
};
